package dev.lingvo.site.components.studio.preview

import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.screenshots.ScreenshotEngine
import dev.lingvo.shared.screenshots.color.IconPaletteExtractor
import dev.lingvo.shared.screenshots.color.PaletteDeriver
import dev.lingvo.shared.screenshots.doc.BrandKit
import dev.lingvo.shared.screenshots.doc.FrameKind
import dev.lingvo.shared.screenshots.doc.Palette
import dev.lingvo.shared.screenshots.render.ShotRenderer
import dev.lingvo.shared.screenshots.render.Surface
import dev.lingvo.shared.screenshots.render.TextMeasurer
import dev.lingvo.shared.screenshots.templates.Template
import dev.lingvo.shared.screenshots.templates.TemplateCatalog
import dev.lingvo.shared.screenshots.text.FontCatalog
import dev.lingvo.shared.screenshots.text.FontResolver
import dev.lingvo.shared.screenshots.text.FontStack
import dev.lingvo.shared.screenshots.text.Script
import dev.lingvo.shared.screenshots.text.ScriptResolver
import dev.lingvo.shared.screenshots.text.Segmenter
import dev.lingvo.site.components.studio.canvas.CanvasMeasurer
import dev.lingvo.site.components.studio.canvas.CanvasSurface
import dev.lingvo.site.components.studio.fonts.FontGate
import dev.lingvo.site.components.studio.fonts.IntlSegmenter
import org.w3c.dom.HTMLCanvasElement
import org.w3c.files.Blob

/** Keeps the editor independent of the catalog's implementation while sharing the export renderer. */
interface DesignLooksPort : DesignCatalogPort {
    /** Previews and exports must make the same fitting decisions. */
    fun renderer(): ShotRenderer
    /** Measurement uses the canvas that draws the preview. */
    fun measurerFor(canvas: HTMLCanvasElement): TextMeasurer
    /** Segmentation can use browser dictionaries with a deterministic fallback. */
    val segmenter: Segmenter
    /** Script fallback is shared with exports. */
    val fonts: FontResolver
    /** Locale and caption jointly determine script coverage. */
    val scripts: ScriptResolver
    /** The canvas adapter owns drawing, not the screen. */
    fun surfaceFor(canvas: HTMLCanvasElement): Surface
    /** Preview fitting waits for the exact sample's font subsets. */
    suspend fun ensureFont(stack: FontStack, weight: Int, italic: Boolean, sample: String): Boolean
    /** A supplied sample is labelled in the UI and never saved as a capture. */
    suspend fun sampleScreen(): Blob?
}

/** Uses merged W2-01/02/03 adapters; W2-04 supplies its catalog, palette and sample at installation. */
class EngineDesignLooks(
    private val catalog: TemplateCatalog? = null,
    private val palettes: PaletteDeriver? = null,
    private val icons: IconPaletteExtractor? = null,
    private val sample: suspend () -> Blob? = { null },
) : DesignLooksPort {
    override val available: Boolean get() = catalog != null && palettes != null && icons != null
    override fun layouts(): List<Template> = catalog?.launchLayouts.orEmpty()
    override fun templates(): List<Template> = catalog?.all.orEmpty()
    override fun palette(template: Template, brand: BrandKit): Palette =
        checkNotNull(palettes) { "The screenshot looks catalog is unavailable." }.derive(template, brand)
    override fun iconPalette(rgba: IntArray, w: Int, h: Int): List<String> =
        checkNotNull(icons) { "The screenshot icon palette is unavailable." }.extract(rgba, w, h)
    override fun framesFor(device: DeviceClass): List<FrameKind> = ScreenshotEngine.frames.offeredFor(device)
    override fun defaultFrame(device: DeviceClass): FrameKind = ScreenshotEngine.frames.defaultFor(device)
    override fun fontFamilies(): List<FontChoice> = FontCatalog.families.map { FontChoice(it.name, it.scripts) }
    override fun renderer(): ShotRenderer = ScreenshotEngine.renderer()
    override fun measurerFor(canvas: HTMLCanvasElement): TextMeasurer = CanvasMeasurer(canvas.getContext("2d"))
    override val segmenter: Segmenter = IntlSegmenter()
    override val fonts: FontResolver = ScreenshotEngine.fonts
    override val scripts: ScriptResolver = ScreenshotEngine.scripts
    override fun surfaceFor(canvas: HTMLCanvasElement): Surface = CanvasSurface(canvas.getContext("2d"))
    override suspend fun ensureFont(stack: FontStack, weight: Int, italic: Boolean, sample: String): Boolean =
        FontGate.ensure(stack, weight, italic, sample)
    override suspend fun sampleScreen(): Blob? = sample()
}

/** Installs once, including for direct entry into the screenshots area. */
object DesignLooks {
    private var installed: DesignLooksPort? = null

    /** First-use installation also serves isolated route hosts that do not compose AppEntry. */
    var current: DesignLooksPort
        get() = installed ?: installProduction()
        set(value) { installed = value }

    fun installProduction(): DesignLooksPort = installed ?: installDesignLooks(::productionDesignLooks).also { installed = it }
}

/** Provider construction can fail without taking existing-design review down with it. */
internal fun installDesignLooks(factory: () -> DesignLooksPort): DesignLooksPort =
    try { factory() } catch (_: Exception) { EngineDesignLooks() }
